import {
  ChoiceInput,
  Divider,
  Flex,
  Input,
  Text,
  TextToolTip,
} from '@components';
import {
  useUpdateUserInfoMutation,
  useUserQuery,
} from '@features/api/userSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { updateBankDetailsFormSchema } from '@validationSchemas/updateProfile';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { StyledButton } from './UpdateBankDetails.styles';
import {
  UpdateBankDetailsFormData,
  UpdateBankDetailsProps,
} from './UpdateBankDetails.types';
import { ibanRegex } from '@utils/validation/consts';
import { useEffect } from 'react';
import { yesNoChoices } from '@utils/choiceValues/choiceValues';
import { useTheme } from '@mui/material';

const UpdateBankDetails: React.FC<UpdateBankDetailsProps> = ({ onClose }) => {
  const intl = useIntl();
  const theme = useTheme();

  const [updateUserInfoMutation] = useUpdateUserInfoMutation();
  const { data: user } = useUserQuery();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, dirtyFields },
    setValue,
    clearErrors,
    trigger,
    watch,
    getValues,
  } = useForm<UpdateBankDetailsFormData>({
    resolver: yupResolver(updateBankDetailsFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      bankAccountHolderName: user?.bankDetails?.accountHolderName ?? '',
      bankLocation: user?.bankDetails?.bankLocation ?? '',
      bankName: user?.bankDetails?.bankName ?? '',
      isNominee:
        user?.bankDetails?.soleAccountOwner !== undefined
          ? !user?.bankDetails?.soleAccountOwner
          : undefined,
      ...(user?.bankDetails?.accountNumber &&
        !user?.bankDetails.iban && {
          bankAccountNumber: user.bankDetails.accountNumber,
          swiftOrBic: user?.bankDetails.swiftOrBic,
        }),
      ...(user?.bankDetails?.iban &&
        !user?.bankDetails.accountNumber && {
          iban: user.bankDetails.iban,
        }),
    },
  });

  const iban = watch('iban');

  useEffect(() => {
    const iban = getValues('iban');
    if (!iban) return;

    setValue(
      'iban',
      iban
        .replace(ibanRegex, '')
        .replace(/(.{4})/g, '$1 ')
        .trim()
    );
    trigger('iban');
  }, [iban, trigger, setValue, getValues]);

  const bankAccountNumber = watch('bankAccountNumber');
  const swiftOrBic = watch('swiftOrBic');

  useEffect(() => {
    if (iban) {
      setValue('bankAccountNumber', '');
      setValue('swiftOrBic', '');
      clearErrors(['bankAccountNumber', 'swiftOrBic']);
    }
    if (bankAccountNumber || swiftOrBic) {
      setValue('iban', '');
      clearErrors('iban');
    }
  }, [iban, bankAccountNumber, swiftOrBic, setValue, clearErrors]);

  const onSubmit = async ({
    bankAccountHolderName,
    bankLocation,
    bankName,
    iban,
    swiftOrBic,
    isNominee,
  }: UpdateBankDetailsFormData) => {
    if (!user) return;
    await updateUserInfoMutation({
      ...user,
      bankDetails: {
        ...user.bankDetails,
        accountHolderName: bankAccountHolderName ?? undefined,
        bankLocation: bankLocation ?? undefined,
        bankName: bankName ?? undefined,
        iban: iban ?? undefined,
        accountNumber: bankAccountNumber ?? undefined,
        swiftOrBic: swiftOrBic ?? undefined,
        soleAccountOwner: !isNominee,
      },
    }).unwrap();
    onClose();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex flexDirection='column' gap={12} mt={10}>
        <Input
          label={intl.formatMessage({
            id: 'investment.bankDetails.accountHolderName',
          })}
          fullWidth
          inputProps={{
            'data-testid': 'account-holder-name',
          }}
          {...registerInput(register, 'bankAccountHolderName')}
          {...getErrorProps(intl, errors.bankAccountHolderName)}
        />
        <Input
          label={intl.formatMessage({
            id: 'investment.bankDetails.bankName',
          })}
          fullWidth
          inputProps={{
            'data-testid': 'bank-name',
          }}
          {...registerInput(register, 'bankName')}
          {...getErrorProps(intl, errors.bankName)}
        />
        <Input
          label={intl.formatMessage({
            id: 'investment.bankDetails.bankLocation',
          })}
          fullWidth
          inputProps={{
            'data-testid': 'bank-location',
          }}
          {...registerInput(register, 'bankLocation')}
          {...getErrorProps(intl, errors.bankLocation)}
        />
        <Flex flexDirection='column' rowGap={10}>
          <Text
            variant='h6'
            fontWeight='fontWeightSemiBold'
            color={theme.palette.black.A100}
          >
            <FormattedMessage id='investment.bankDetails.bankAccountIdentifier' />
          </Text>
          <Input
            label={intl.formatMessage({
              id: 'investment.bankDetails.iban',
            })}
            fullWidth
            inputProps={{
              'data-testid': 'iban',
              sx: {
                textTransform: 'uppercase',
              },
            }}
            disabled={!!bankAccountNumber || !!swiftOrBic}
            {...registerInput(register, 'iban')}
            {...getErrorProps(intl, errors.iban)}
          />
          <Divider>OR</Divider>
          <Flex flexDirection='column' gap={12}>
            <Input
              label={intl.formatMessage({
                id: 'investment.bankDetails.bankAccountNumber',
              })}
              inputProps={{
                'data-testid': 'bank-account-number',
                sx: {
                  textTransform: 'uppercase',
                },
              }}
              fullWidth
              disabled={!!iban}
              {...registerInput(register, 'bankAccountNumber')}
              {...getErrorProps(intl, errors.bankAccountNumber)}
            />
            <Input
              label={intl.formatMessage({
                id: 'investment.bankDetails.swiftAndBic',
              })}
              inputProps={{
                'data-testid': 'swift-Bic',
                sx: {
                  textTransform: 'uppercase',
                },
              }}
              fullWidth
              disabled={!!iban}
              {...registerInput(register, 'swiftOrBic')}
              {...getErrorProps(intl, errors.swiftOrBic)}
            />
          </Flex>
        </Flex>
        <Flex flexDirection='column'>
          <TextToolTip
            text={intl.formatMessage({
              id: 'investment.bankDetails.soleBeneficialOwnerAccount',
            })}
            toolTipText={intl.formatMessage({
              id: 'investment.bankDetails.soleBeneficialOwnerAccountTooltipMessage',
            })}
          />
          <Controller
            control={control}
            name='isNominee'
            render={({ field: { ref, value, ...restProps } }) => (
              <ChoiceInput
                direction='row'
                fullWidth
                choices={yesNoChoices}
                defaultValues={value !== undefined ? [value] : undefined}
                {...restProps}
              />
            )}
          />
        </Flex>
        <StyledButton
          disabled={!isValid || Object.keys(dirtyFields).length === 0}
          type='submit'
          data-testid='bank-save-button'
        >
          <FormattedMessage id='misc.save' />
        </StyledButton>
      </Flex>
    </form>
  );
};

export default UpdateBankDetails;
